import React, { useEffect, useState } from "react";
import Menu from "../panel/Menu";
import {
    Box,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Divider,
    TextField,
    Button,
    IconButton
} from "@mui/material";
import { ArrowBack, ArrowForward } from "@mui/icons-material";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
    collection,
    getDocs,
    doc,
    setDoc,
    query,
    where
} from "firebase/firestore";
import { db } from "../firebase";
import { addDays, format } from "date-fns";
import { ToastContainer, toast } from "react-toastify";
import Swal from "sweetalert2";
import "react-toastify/dist/ReactToastify.css";
import { LinearProgress } from "@mui/material";

/* ===== CONSTANTS ===== */
const COL_WIDTH = 120;
const LEFT_COL_WIDTH = 260;
const PAGE_SIZE = 7;

function Rateupdates() {
    const auth = getAuth();

    const [userId, setUserId] = useState(null);
    const [propertyId, setPropertyId] = useState(null);

    const [rooms, setRooms] = useState([]);
    const [plans, setPlans] = useState({});
    const [roomPlans, setRoomPlans] = useState([]);

    const [rates, setRates] = useState({});
    const [availability, setAvailability] = useState({});
    const [savingRoomId, setSavingRoomId] = useState(null);

    const [dates, setDates] = useState([]);
    const [page, setPage] = useState(0);

    /* ================= AUTH ================= */
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (user) => {
            if (!user) return;
            setUserId(user.uid);

            const snap = await getDocs(collection(db, "authroles"));
            snap.forEach(d => {
                if (d.id === user.uid) {
                    setPropertyId(d.data().propertyId);
                }
            });
        });
        return () => unsub();
    }, []);

    /* ================= LOAD DATA ================= */
    useEffect(() => {
        if (!userId || !propertyId) return;
        loadAll();
    }, [userId, propertyId]);

    const loadAll = async () => {
        /* ROOMS */
        const roomSnap = await getDocs(collection(db, "rooms"));
        setRooms(
            roomSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(r => r.userId === userId && r.propertyId === propertyId)
        );

        /* RATE PLANS */
        const planSnap = await getDocs(collection(db, "rate_plans"));
        const planMap = {};
        planSnap.docs.forEach(d => {
            planMap[d.id] = d.data().planName;
        });
        setPlans(planMap);

        /* ROOM ↔ PLAN */
        const tariffSnap = await getDocs(collection(db, "room_tariffs"));
        setRoomPlans(
            tariffSnap.docs
                .map(d => ({ id: d.id, ...d.data() }))
                .filter(t => t.userId === userId && t.propertyId === propertyId)
        );

        /* EXISTING RATES */
        const rateSnap = await getDocs(
            query(
                collection(db, "room_rates_calendar"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );
        const rateMap = {};
        rateSnap.docs.forEach(d => {
            const r = d.data();
            rateMap[`${r.roomId}_${r.ratePlanId}_${r.date}`] = r.saleRate;
        });
        setRates(rateMap);

        /* AVAILABILITY */
        const availSnap = await getDocs(
            query(
                collection(db, "room_availability_calendar"),
                where("userId", "==", userId),
                where("propertyId", "==", propertyId)
            )
        );
        const availMap = {};
        availSnap.docs.forEach(d => {
            const a = d.data();
            availMap[`${a.roomId}_${a.date}`] = a.quantity;
        });
        setAvailability(availMap);

    };

    /* ================= DATES ================= */
    useEffect(() => {
        const arr = [];
        for (let i = 0; i < 180; i++) {
            arr.push(addDays(new Date(), i));
        }
        setDates(arr);
    }, []);

    const visibleDates = dates.slice(
        page * PAGE_SIZE,
        page * PAGE_SIZE + PAGE_SIZE
    );

    /* ================= CHANGE HANDLERS ================= */
    const changeQty = (roomId, date, val) => {
        setAvailability(p => ({ ...p, [`${roomId}_${date}`]: val }));
    };

    const changeRate = (roomId, planId, date, val) => {
        setRates(p => ({ ...p, [`${roomId}_${planId}_${date}`]: val }));
    };

    /* ================= BULK UPDATE ================= */
    const bulkQty = async (roomId) => {
        const { value } = await Swal.fire({
            title: "Bulk Availability",
            input: "number",
            inputLabel: "Available rooms for all future dates",
            showCancelButton: true
        });
        if (value === undefined) return;

        const updated = { ...availability };
        dates.forEach(d => {
            updated[`${roomId}_${format(d, "yyyy-MM-dd")}`] = value;
        });
        setAvailability(updated);
        toast.success("Bulk availability applied");
    };

    const bulkRate = async (roomId, planId) => {
        const { value } = await Swal.fire({
            title: "Bulk Rate",
            input: "number",
            inputLabel: "Rate for all future dates",
            showCancelButton: true
        });
        if (value === undefined) return;

        const updated = { ...rates };
        dates.forEach(d => {
            updated[`${roomId}_${planId}_${format(d, "yyyy-MM-dd")}`] = value;
        });
        setRates(updated);
        // toast.success("Bulk rate applied");
    };

    /* ================= SAVE ================= */
    const saveRoom = async (roomId) => {
        try {
            setSavingRoomId(roomId); // 🔥 START LOADER

            for (let d of dates) {
                const date = format(d, "yyyy-MM-dd");

                const plansForRoom = roomPlans.filter(p => p.roomId === roomId);

                for (let p of plansForRoom) {
                    const rateKey = `${roomId}_${p.ratePlanId}_${date}`;

                    if (!rates[rateKey]) continue;

                    await setDoc(doc(db, "room_rates_calendar", rateKey), {
                        roomId,
                        ratePlanId: p.ratePlanId,
                        planName: plans[p.ratePlanId],
                        date,
                        saleRate: Number(rates[rateKey]),
                        userId,
                        propertyId,
                        updatedAt: new Date()
                    });
                }
            }

            toast.success("Rates saved successfully");
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setSavingRoomId(null); // ✅ STOP LOADER
        }
    };


    const isWeekend = (date) => {
        const day = format(date, "EEE");
        return day === "Sat" || day === "Sun";
    };


    return (
        <Box display="flex" minHeight="100vh" bgcolor="#f4f6f8">
            <Menu />

            <Box flex={1} p={3} className="content-wrapper">
                <ToastContainer autoClose={2000} />

                <Typography variant="h5" fontWeight={800} mb={2}>
                    Room Availability & Rates
                </Typography>

                <Card sx={{ borderRadius: 3, boxShadow: "0 10px 30px rgba(0,0,0,0.08)" }}>

                    <CardContent>
                        {/* NAV */}
                        <Box display="flex" justifyContent="space-between" mb={1}>
                            <IconButton disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                                <ArrowBack />
                            </IconButton>
                            <IconButton
                                disabled={(page + 1) * PAGE_SIZE >= dates.length}
                                onClick={() => setPage(p => p + 1)}
                            >
                                <ArrowForward />
                            </IconButton>
                        </Box>

                        {/* GRID */}
                        <Box sx={{ overflowX: "auto" }}>
                            {/* HEADER */}
                            <Box sx={{ display: "flex", position: "sticky", top: 0, zIndex: 2, bgcolor: "#fff" }}>
                                <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1, fontWeight: 700 }}>
                                    Room / Plan
                                </Box>
                                {visibleDates.map(d => {
                                    const weekend = isWeekend(d);

                                    return (
                                        <Box
                                            key={d}
                                            sx={{
                                                width: COL_WIDTH,
                                                textAlign: "center",
                                                p: 1,
                                                bgcolor: weekend ? "#fff5f5" : "transparent",
                                                borderLeft: weekend ? "1px solid #f1caca" : "1px solid #eee"
                                            }}
                                        >
                                            <Typography
                                                fontSize={12}
                                                fontWeight={600}
                                                color={weekend ? "error.main" : "text.secondary"}
                                            >
                                                {format(d, "EEE")}
                                            </Typography>
                                            <Typography
                                                fontSize={13}
                                                fontWeight={700}
                                                color={weekend ? "error.main" : "text.primary"}
                                            >
                                                {format(d, "dd MMM")}
                                            </Typography>
                                        </Box>
                                    );
                                })}

                            </Box>

                            {/* BODY */}
                            {rooms.map(room => {
                                const plansForRoom = roomPlans.filter(p => p.roomId === room.id);

                                return (
                                    <Box key={room.id}>
                                        {/* AVAILABILITY */}
                                        {/* <Box sx={{ display: "flex", bgcolor: "#fafafa" }}>
                                            <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1, fontWeight: 600 }}>
                                                {room.roomTypeName} (Qty)
                                                <Button size="small" sx={{ ml: 1 }} onClick={() => bulkQty(room.id)}>
                                                    Bulk
                                                </Button>
                                            </Box>
                                            {visibleDates.map(d => {
                                                const date = format(d, "yyyy-MM-dd");
                                                return (
                                                    <Box
                                                        key={date}
                                                        sx={{
                                                            width: COL_WIDTH,
                                                            p: 0.5,
                                                            bgcolor: isWeekend(d) ? "#fffafa" : "transparent"
                                                        }}
                                                    >

                                                        <TextField
                                                            size="small"
                                                            type="number"
                                                            fullWidth
                                                            value={availability[`${room.id}_${date}`] || ""}
                                                            onChange={e => changeQty(room.id, date, e.target.value)}
                                                        />
                                                    </Box>
                                                );
                                            })}
                                        </Box> */}

                                        {/* RATES */}
                                        {plansForRoom.map(p => (
                                            <Box key={p.id} sx={{ display: "flex", borderBottom: "1px solid #eee" }}>
                                                <Box sx={{ minWidth: LEFT_COL_WIDTH, p: 1 }}>
                                                    {plans[p.ratePlanId]}
                                                    <Button
                                                        size="small"
                                                        sx={{ ml: 1 }}
                                                        onClick={() => bulkRate(room.id, p.ratePlanId)}
                                                    >
                                                        Bulk
                                                    </Button>
                                                </Box>
                                                {visibleDates.map(d => {
                                                    const date = format(d, "yyyy-MM-dd");
                                                    return (
                                                        <Box key={date} sx={{ width: COL_WIDTH, p: 0.5 }}>
                                                            <TextField
                                                                size="small"
                                                                type="number"
                                                                fullWidth
                                                                value={rates[`${room.id}_${p.ratePlanId}_${date}`] || ""}
                                                                onChange={e =>
                                                                    changeRate(room.id, p.ratePlanId, date, e.target.value)
                                                                }
                                                            />
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        ))}

                                        <Box textAlign="left" my={1}>
                                            <Button
                                                variant="contained"
                                                disabled={savingRoomId === room.id}
                                                onClick={() => saveRoom(room.id)}
                                                sx={{ minWidth: 180 }}
                                            >
                                                {savingRoomId === room.id ? "Saving..." : `Save ${room.roomTypeName}`}
                                            </Button>

                                            {savingRoomId === room.id && (
                                                <LinearProgress
                                                    sx={{ mt: 1, borderRadius: 1 }}
                                                />
                                            )}
                                        </Box>

                                    </Box>
                                );
                            })}
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        </Box>
    );
}

export default Rateupdates;
